/**
 * Region Utilities
 *
 * Country-to-region mapping logic matching the ftld-ticketing-api
 */

// Country code lists
export const US_COUNTRIES = ['FM', 'GU', 'PR', 'US', 'VI'] as const;

export const EU_EEA_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'MC',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'CH',
  'GB',
  'UK',
] as const;

export const ASIA_COUNTRIES = [
  'BT',
  'BN',
  'CN',
  'HK',
  'ID',
  'MO',
  'MY',
  'PH',
  'SG',
  'KR',
  'SR',
  'TW',
  'TH',
  'VN',
  'KP',
] as const;

export type USCountry = typeof US_COUNTRIES[number];
export type EUEEACountry = typeof EU_EEA_COUNTRIES[number];
export type AsiaCountry = typeof ASIA_COUNTRIES[number];
export type CountryCode = USCountry | EUEEACountry | AsiaCountry | string;

// Region definitions
export const REGIONS = [
  { value: 'US', label: 'US & Territories' },
  { value: 'EU-EEA', label: 'EU / EEA' },
  { value: 'ASIA', label: 'Asia' },
  { value: 'INT', label: 'International (Non-US)' },
  { value: 'INT excluding ASIA', label: 'International (excl. Asia)' },
  { value: 'INT excluding EU-EEA', label: 'International (excl. EU/EEA)' },
  { value: 'OTHER', label: 'Other' },
] as const;

export type Region = typeof REGIONS[number]['value'];

/**
 * Determine the primary region for a country code
 */
export function getRegion(country: CountryCode | null | undefined): Region | null {
  if (!country)
    return null;

  const upperCountry = country.toUpperCase();

  if (US_COUNTRIES.includes(upperCountry as USCountry)) {
    return 'US';
  }

  if (EU_EEA_COUNTRIES.includes(upperCountry as EUEEACountry)) {
    return 'EU-EEA';
  }

  if (ASIA_COUNTRIES.includes(upperCountry as AsiaCountry)) {
    return 'ASIA';
  }

  // If not in any known region, it's "OTHER"
  return 'OTHER';
}

/**
 * Check if a country is in a given region
 * Used for filtering (supports compound regions like INT, INT excluding ASIA, etc.)
 */
export function isInRegion(country: CountryCode | null | undefined, region: Region): boolean {
  if (!country)
    return false;

  const upperCountry = country.toUpperCase();

  if (region === 'INT') {
    return !US_COUNTRIES.includes(upperCountry as USCountry);
  }

  if (region === 'INT excluding ASIA') {
    return (
      !US_COUNTRIES.includes(upperCountry as USCountry)
      && !ASIA_COUNTRIES.includes(upperCountry as AsiaCountry)
    );
  }

  if (region === 'INT excluding EU-EEA') {
    return (
      !US_COUNTRIES.includes(upperCountry as USCountry)
      && !EU_EEA_COUNTRIES.includes(upperCountry as EUEEACountry)
    );
  }

  switch (region) {
    case 'US': {
      return US_COUNTRIES.includes(upperCountry as USCountry);
    }
    case 'EU-EEA': {
      return EU_EEA_COUNTRIES.includes(upperCountry as EUEEACountry);
    }
    case 'ASIA': {
      return ASIA_COUNTRIES.includes(upperCountry as AsiaCountry);
    }
    case 'OTHER': {
      return (
        !US_COUNTRIES.includes(upperCountry as USCountry)
        && !EU_EEA_COUNTRIES.includes(upperCountry as EUEEACountry)
        && !ASIA_COUNTRIES.includes(upperCountry as AsiaCountry)
      );
    }
    default: {
      return false;
    }
  }
}

/**
 * Get a human-readable label for a region
 */
export function getRegionLabel(region: Region | null | undefined): string {
  if (!region)
    return '—';
  const found = REGIONS.find((r) => r.value === region);
  return found?.label ?? region;
}
